// @ts-nocheck
import * as React from "react";
import { siteRegion, BRAND_IMG, NCARE, Ic, PasswordInput, AppFooter, apiErrorMessage, villageEnabled } from "./common";

// Auth — real login (1280x832 design viewport).
//
// 단일 로그인 화면. "통합돌봄 / 마을교육공동체" 모드 토글이 있고 모드가 바뀌면
// 좌측 브랜드 패널 카피·역할 카드 필터·로그인 성공 리다이렉트가 함께 전환된다.
// 세션/계정은 공용(/api/v1/sessions) — 분기는 UI 측면만.

const SAVED_EMAIL_KEY = "care_saved_email";

// Village 모듈 서브 브랜드 녹색 (돌봄연계국 C 식별 포인트) — 모드 토글/배지에만 사용.
const VILLAGE_GREEN = { main: "#4F8A72", soft: "#D7E5CA", deep: "#2F5A3E" };

// 모드별 설정 — 역할 필터·라벨 재해석·리다이렉트·카피.
//
// 세션/계정은 공용(User.role)이므로 데모 역할 키(demoRoles[].key)는 통합돌봄 기준
// 그대로 두고, 모드별로 "노출 여부 + 표기(label/desc)" 만 재해석한다. 마을교육공동체
// 맥락에서 "통합돌봄 운영자"는 "교육공동체 운영자", "보호자"는 "학부모" 가 되는 식.
type AuthMode = "care" | "village";

interface RoleOverride { label?: string; desc?: string; }

const MODE_CONFIG: Record<AuthMode, {
  label: string;
  // demoRoles.key 중 노출할 집합(undefined = 전부).
  roleKeys?: string[];
  // 노출된 역할 카드의 라벨/설명 재해석.
  roleOverrides?: Record<string, RoleOverride>;
  // 성공 후 next 가 없을 때 쓸 폴백 경로. 모드별로 다름.
  defaultLanding: (defaultPath?: string) => string;
}> = {
  care: {
    label: "통합돌봄",
    defaultLanding: (d) => d || "/",
  },
  village: {
    label: "마을교육공동체",
    // Village 접점이 있는 User.role 만 노출(어르신·케어매니저·유관기관 숨김).
    // coordinator 는 demo 계정이 없으면 availableRoles 교집합에서 자동 제외되므로 안전.
    roleKeys: ["system_admin", "region_admin", "coordinator", "guardian"],
    roleOverrides: {
      region_admin: { label: "교육공동체 운영자", desc: "교육국 승인·커리큘럼 발행" },
      coordinator:  { label: "코디네이터",         desc: "권역 조정·프로그램 운영" },
      guardian:     { label: "학부모",             desc: "자녀 참여·공의 투표·민보 관찰" },
    },
    defaultLanding: () => "/village",
  },
};

// 초기 모드 — URL 경로가 /village/login 이면 village, 아니면 care.
function initialModeFromPath(): AuthMode {
  if (typeof window === "undefined") return "care";
  return window.location.pathname === "/village/login" ? "village" : "care";
}

// 로그인 후 돌아갈 경로 — URL 쿼리 ?next=... 를 안전하게 해석한다.
// 오픈 리다이렉트 방지: 반드시 "/" 로 시작하는 **상대 경로**만 허용(스킴·호스트 금지).
// 현재는 /village 모듈 하위만 명시 허용(향후 다른 서브셸이 생기면 여기에 추가).
function nextTargetFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("next");
  if (!raw) return null;
  // 반드시 단일 슬래시로 시작하는 상대 경로(스킴/프로토콜-상대 URL 차단).
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  // 허용 목록: /village 와 /village#... /village?...
  if (raw === "/village" || raw.startsWith("/village#") || raw.startsWith("/village?")) return raw;
  return null;
}

function Auth() {
  // "아이디 저장": 마지막으로 저장한 이메일을 localStorage에서 읽어 프리필하고,
  // 저장된 값이 있으면 체크박스를 켠 상태로 시작한다(비밀번호는 저장하지 않음).
  const savedEmail = (() => { try { return localStorage.getItem(SAVED_EMAIL_KEY) || ""; } catch { return ""; } })();
  const [email, setEmail] = React.useState(savedEmail);
  const [password, setPassword] = React.useState("");
  const [saveId, setSaveId] = React.useState(!!savedEmail);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [demoRole, setDemoRole] = React.useState(null); // 진행 중인 로그인 유형 키(스피너용)
  const [siteRoles, setSiteRoles] = React.useState([]);     // 사이트 권역에서 로그인 가능한 역할 키
  const [sysAdmin, setSysAdmin] = React.useState(false);    // system_admin 계정 가용 여부(전역)
  const [mode, setMode] = React.useState<AuthMode>(initialModeFromPath);

  // Village 모듈 플래그 — 토글 노출 조건. off 면 통합돌봄 모드만.
  const villageOn = villageEnabled();
  // 플래그 off 상태에서 /village/login 으로 들어온 경우(엣지) — care 모드로 강제.
  React.useEffect(() => {
    if (!villageOn && mode === "village") setMode("care");
  }, [villageOn, mode]);
  // 모드 전환 시 URL 경로도 동기화해 북마크/뒤로가기 UX 를 명확히(새로고침 없는 replaceState).
  const switchMode = React.useCallback((next: AuthMode) => {
    setMode(next);
    const target = next === "village" ? "/village/login" : "/login";
    if (typeof window !== "undefined" && window.location.pathname !== target) {
      window.history.replaceState(null, "", target + window.location.search + window.location.hash);
    }
  }, []);

  // 이 사이트는 남양주 전용이다 — 권역 선택 없이 사이트 권역(Region.site_default)으로 고정.
  const region   = siteRegion();
  const regionId = region.id;
  const cityName = (region.name || "").replace(/시$/, "");

  // 로그인 유형: 유형을 고르면 서버가 (사이트 권역, 역할)에 맞는 대표 계정으로
  // 세션을 발급한다. 비밀번호는 프런트에 두지 않는다(서버가 DB에서 선택).
  // 단일 권역(남양주) 사이트라 라벨에서 "권역" 용어를 걷어냈다.
  const demoRoles = [
    { key: "system_admin", label: "시스템 관리자",   desc: "플랫폼 전체 관리",       icon: Ic.dashboard },
    { key: "region_admin", label: "통합돌봄 운영자", desc: "운영·승인·매칭 총괄",    icon: Ic.region },
    { key: "manager",      label: "케어매니저",       desc: "모바일 방문·케이스",     icon: Ic.manager },
    { key: "agency",       label: "유관기관",         desc: "기관 대시보드·지도",     icon: Ic.org },
    { key: "guardian",     label: "보호자",           desc: "어르신 상태·알림",       icon: Ic.heart },
    { key: "elderly",      label: "어르신",           desc: "복약·일정 홈",           icon: Ic.user },
  ];

  // 사이트 권역에서 실제 로그인 가능한 역할만 카드로 노출한다(계정 없는 유형은 숨김).
  // 응답은 전체 권역 목록이라 사이트 권역 한 건만 골라 쓴다.
  React.useEffect(() => {
    fetch("/api/v1/sessions/demo_regions", { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : { regions: [] })
      .then(j => {
        const mine = (j.regions || []).find(r => r.id === regionId);
        setSiteRoles(mine ? mine.roles : []);
        setSysAdmin(!!j.system_admin);
      })
      .catch(() => {});
  }, [regionId]);

  // system_admin(전역) 카드는 가용하면 항상 맨 앞에, 이어서 사이트 권역의 역할.
  const availableRoles = (sysAdmin ? ["system_admin"] : []).concat(siteRoles);
  // 모드별 역할 필터(마을교육공동체 모드는 어르신/매니저/유관기관 숨김) + 라벨 재해석.
  const modeRoleKeys = MODE_CONFIG[mode].roleKeys;
  const roleOverrides = MODE_CONFIG[mode].roleOverrides || {};
  const visibleRoles = demoRoles
    .filter(d => availableRoles.includes(d.key))
    .filter(d => !modeRoleKeys || modeRoleKeys.includes(d.key))
    .map(d => {
      const ov = roleOverrides[d.key];
      return ov ? { ...d, label: ov.label || d.label, desc: ov.desc || d.desc } : d;
    });

  // 성공 후 리다이렉트 — ?next=/village/... 가 있으면 우선, 모드별 폴백은 그 다음.
  const landingAfterLogin = (defaultPath?: string) => {
    const next = nextTargetFromUrl();
    return next || MODE_CONFIG[mode].defaultLanding(defaultPath);
  };

  const demoLogin = async (role) => {
    if (loading || demoRole) return;
    setErr(null);
    setDemoRole(role);
    try {
      const body = role === "system_admin" ? { role } : { role, region: regionId };
      const r = await fetch("/api/v1/sessions/demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin",
      });
      if (r.ok) {
        const u = await r.json();
        window.location.href = u.must_change_password ? "/password-change" : landingAfterLogin(u.default_path);
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(apiErrorMessage(j, r.status));
        setDemoRole(null);
      }
    } catch (e) { setErr(apiErrorMessage(e)); setDemoRole(null); }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const r = await fetch("/api/v1/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "same-origin",
      });
      if (r.ok) {
        // 로그인 성공 시에만 이메일을 저장/삭제(실패한 오타 이메일은 남기지 않음).
        try {
          if (saveId) localStorage.setItem(SAVED_EMAIL_KEY, email);
          else localStorage.removeItem(SAVED_EMAIL_KEY);
        } catch {}
        const u = await r.json();
        // 비밀번호 만료 시 강제 변경 화면. 그 외에는 모드별 폴백(마을교육공동체 모드는
        // /village, 통합돌봄 모드는 역할 default_path) — ?next=... 가 있으면 우선.
        window.location.href = u.must_change_password ? "/password-change" : landingAfterLogin(u.default_path);
      } else {
        const j = await r.json().catch(() => ({}));
        // 서버가 주는 친화 메시지(계정 잠금/탈퇴 안내 등)를 그대로 살리고,
        // 없으면 코드맵/상태 폴백. 기계용 코드는 노출하지 않는다.
        setErr(apiErrorMessage(j, r.status));
      }
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setLoading(false); }
  };

  const trust = [
    { t: '민관협력', d: '실행거점 운영' },
    { t: 'AI 매칭', d: '최적 케어 제안' },
    { t: '감사 보존', d: '5년 추적' },
  ];

  return (
    <div className="app" style={{display:'flex', flexDirection:'column', minHeight:'100vh', background:'var(--bg)'}}>
    <div className="auth-grid">
      {/* 좁은 화면에서는 숨겨지는 브랜드 패널 (CSS .auth-brand) — 남양주 N-Care 2.0 */}
      {/* 좌측 패널 — 배경은 표지 사진 전체(잘림 없이 contain, 하단 정렬) + 상단이 진한
          네이비 스크림. 글자는 사진 위가 아니라 스크림이 두꺼운 상단에 모아 배치한다.
          (좁은 화면에서는 CSS .auth-brand 가 통째로 숨겨진다) */}
      <div className="auth-brand" style={{
             justifyContent:'flex-start',
             backgroundImage:`linear-gradient(180deg, rgba(14,36,56,0.97) 0%, rgba(16,40,62,0.95) 34%, rgba(20,48,74,0.55) 48%, rgba(20,48,74,0.18) 62%, rgba(20,48,74,0.42) 100%), url(${BRAND_IMG.photo})`,
             backgroundSize:'cover, contain',
             backgroundPosition:'center, bottom center',
             backgroundRepeat:'no-repeat, no-repeat',
             backgroundColor: NCARE.ink}}>
        <a href="/" style={{display:'flex', alignItems:'center', gap:11, textDecoration:'none', color:'inherit'}}>
          <img src={BRAND_IMG.mark} alt="" width={38} height={38}
               style={{width:38, height:38, borderRadius:11, objectFit:'cover', display:'block'}}/>
          <div>
            <div style={{fontWeight:800, fontSize:15, color:'#fff'}}>{cityName}형 통합돌봄</div>
            <div style={{fontSize:10.5, color:'#9FC4E0', letterSpacing:'0.1em', fontWeight:700}}>N-CARE 2.0</div>
          </div>
        </a>

        <div style={{marginTop:'clamp(28px, 6vh, 56px)', maxWidth:560}}>
          <div style={{fontSize:12, color:'#9FC4E0', fontWeight:700, letterSpacing:'0.02em', marginBottom:12}}>2026 Namyangju Strategic Initiative</div>
          <h1 style={{fontSize:'clamp(26px, 2.4vw, 36px)', fontWeight:800, lineHeight:1.25, letterSpacing:'-0.02em', margin:0, color:'#fff', wordBreak:'keep-all'}}>
            2026 {cityName}형 통합돌봄<br/>‘N-Care 2.0’ 하이브리드 전략
          </h1>
          <p style={{fontSize:13.5, lineHeight:1.75, color:'#C7D8E6', marginTop:14, maxWidth:470, wordBreak:'keep-all'}}>
            초고령사회와 「돌봄통합지원법」 시행에 따른 민관협력 실행 로드맵.
            케어매니저·유관기관·보호자·운영자가 같은 케이스를 함께 봅니다.
          </p>
        </div>

        {/* 아래 여백은 배경 사진(전체)이 채운다. minHeight를 크게 잡으면 낮은 창(≤820px)에서
            하단 신뢰 배지가 잘리므로(패널 overflow:hidden) 최소값만 두고 flex로 늘린다. */}
        <div style={{flex:1, minHeight:80}}/>

        <div style={{fontSize:13, color:'#DCE8F2', fontStyle:'italic', textShadow:'0 1px 12px rgba(6,18,30,0.9)'}}>
          “살던 곳에서 365일 존엄하게, {cityName} N-Care가 함께합니다”
        </div>
        <div style={{display:'flex', gap:28, marginTop:16, paddingTop:16, borderTop:'1px solid rgba(255,255,255,0.18)'}}>
          {trust.map((x,i)=>(
            <div key={i}>
              <div style={{fontSize:13, fontWeight:800, color:'#fff', textShadow:'0 1px 10px rgba(6,18,30,0.9)'}}>{x.t}</div>
              <div style={{fontSize:11, color:'#BFD4E5', marginTop:3, textShadow:'0 1px 10px rgba(6,18,30,0.9)'}}>{x.d}</div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="auth-panel">
        <div className="auth-panel-top">
          {/* 모바일 전용 로고 (브랜드 패널이 숨겨질 때 노출) */}
          <a href="/" className="auth-mobile-logo" style={{alignItems:'center', gap:9, textDecoration:'none', color:'inherit'}}>
            <img src={BRAND_IMG.mark} alt="" width={28} height={28}
                 style={{width:28, height:28, borderRadius:8, objectFit:'cover', display:'block'}}/>
            <div>
              <div style={{fontWeight:800, fontSize:14, color:NCARE.navy, lineHeight:1.1}}>{cityName}형 통합돌봄</div>
              <div style={{fontSize:9, color:NCARE.accent, letterSpacing:'0.1em', fontWeight:700}}>N-CARE 2.0</div>
            </div>
          </a>
          <div style={{flex:1}}/>
          <div style={{fontSize:12, color:'var(--ink-3)'}}>
            처음이신가요? <a href="/signup" style={{color:NCARE.navy2, fontWeight:700, marginLeft:6, textDecoration:'none'}}>회원가입 →</a>
          </div>
        </div>

        <div className="auth-form-inner">
          {/* 모드 토글 — Village 플래그 on 일 때만 노출. 선택에 따라 역할 필터·리다이렉트가 바뀐다. */}
          {villageOn && (
            <div role="tablist" aria-label="로그인 모드"
                 style={{
                   display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4,
                   padding: 4, borderRadius: 10,
                   background: "var(--bg-sub, #F4EEDF)",
                   border: "1px solid var(--line, var(--border, #E8DFC9))",
                   marginBottom: 18,
                 }}>
              {(["care", "village"] as AuthMode[]).map((m) => {
                const on = m === mode;
                const accent = m === "village" ? VILLAGE_GREEN.deep : NCARE.navy;
                return (
                  <button
                    key={m} type="button" role="tab" aria-selected={on}
                    onClick={() => switchMode(m)}
                    style={{
                      padding: "8px 10px", borderRadius: 7, border: "none",
                      background: on ? "#fff" : "transparent",
                      color: on ? accent : "var(--ink-3)",
                      fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                      boxShadow: on ? "0 1px 2px rgba(42,36,31,.08)" : "none",
                      fontFamily: "inherit", textAlign: "center",
                    }}>
                    {MODE_CONFIG[m].label}
                  </button>
                );
              })}
            </div>
          )}

          <div style={{marginBottom:24}}>
            <h2 style={{fontSize:28, fontWeight:800, letterSpacing:'-0.02em', margin:0,
                        color: mode === "village" ? VILLAGE_GREEN.deep : NCARE.navy}}>로그인</h2>
            {mode === "village" ? (
              <p style={{color:'var(--ink-3)', fontSize:13, marginTop:8}}>
                <span style={{
                  display:"inline-block", padding:"2px 8px", borderRadius:999,
                  background: VILLAGE_GREEN.soft, color: VILLAGE_GREEN.deep,
                  fontSize:11, fontWeight:700, marginRight:6,
                }}>돌봄연계국 C</span>
                마을교육공동체 모듈 · 통합돌봄 계정으로 접속합니다.
              </p>
            ) : (
              <p style={{color:'var(--ink-3)', fontSize:13, marginTop:8}}>{cityName} N-Care 2.0 통합돌봄 · 가입하신 이메일과 비밀번호로 로그인하세요.</p>
            )}
          </div>

          {err && (
            <div role="alert" style={{display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5, marginBottom:16}}>
              <span style={{width:16, height:16, flex:'0 0 auto', display:'grid', placeItems:'center', fontSize:16}}>{Ic.alert}</span>
              <span>{err}</span>
            </div>
          )}

          <div className="label">이메일</div>
          <input className="input" value={email} onChange={e=>setEmail(e.target.value)} type="email" autoComplete="email" required style={{marginBottom:14}}/>

          <div className="label">비밀번호</div>
          <PasswordInput value={password} onChange={e=>setPassword(e.target.value)}
                         autoComplete="current-password" required/>

          <div style={{display:'flex', gap:8, alignItems:'center', marginTop:18, fontSize:12, color:'var(--ink-3)'}}>
            <label style={{display:'flex', alignItems:'center', gap:6, cursor:'pointer'}}>
              <input type="checkbox" checked={saveId} onChange={e=>setSaveId(e.target.checked)}/> 아이디 저장
            </label>
            <div style={{flex:1}}/>
            <a href="/forgot-password" style={{color:'var(--orange-600)', fontWeight:600, textDecoration:'none'}}>비밀번호 찾기</a>
          </div>

          <button type="submit" disabled={loading || !!demoRole} className="btn btn-lg"
                  style={{
                    marginTop:24, width:'100%',
                    background: mode === "village" ? VILLAGE_GREEN.deep : NCARE.navy,
                    color:'#fff',
                    border:'1px solid ' + (mode === "village" ? VILLAGE_GREEN.deep : NCARE.navy),
                  }}>
            {loading ? '로그인 중…' : '로그인 →'}
          </button>

          {/* 로그인 유형 선택 — 남양주 전용 사이트라 권역 선택 없이 유형만 고른다. */}
          <div style={{marginTop:28}}>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
              <div style={{flex:1, height:1, background:'var(--line)'}}/>
              <span style={{fontSize:11, fontWeight:700, letterSpacing:'0.04em', color:'var(--ink-3)'}}>{cityName} N-Care · 로그인 유형 선택</span>
              <div style={{flex:1, height:1, background:'var(--line)'}}/>
            </div>

            {/* 권역 배지 — 선택이 아니라 "이 사이트가 남양주 전용"이라는 고정 표시. */}
            <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:12,
                         padding:'7px 12px', borderRadius:10, background:NCARE.soft, color:NCARE.navy2, fontSize:11.5, fontWeight:700}}>
              <span style={{width:14, height:14, display:'grid', placeItems:'center', fontSize:14}}>{Ic.region}</span>
              {region.name} 전용
            </div>

            {visibleRoles.length === 0 && (
              <div style={{fontSize:11.5, color:'var(--ink-3)', background:'var(--bg)', border:'1px dashed var(--line)', borderRadius:10, padding:'10px 12px', marginBottom:8, textAlign:'center'}}>
                유형별 체험 계정을 불러오는 중입니다. 위 이메일·비밀번호로 로그인해 주세요.
              </div>
            )}

            {/* 2열 고정이면 좁은 폰(≤360px)에서 버튼이 카드 밖으로 삐져나온다.
                auto-fit 으로 폭이 모자라면 1열로 접고, 그리드 아이템의 기본
                min-width:auto(=콘텐츠 최소폭) 도 minWidth:0 으로 풀어준다. */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(128px, 1fr))', gap:8}}>
              {visibleRoles.map((d) => {
                const busy = demoRole === d.key;
                return (
                  <button
                    key={d.key} type="button" onClick={() => demoLogin(d.key)}
                    disabled={loading || (!!demoRole && !busy)}
                    style={{
                      display:'flex', alignItems:'center', gap:10, textAlign:'left', minWidth:0,
                      padding:'10px 12px', borderRadius:10, cursor:'pointer',
                      border:'1px solid var(--line)', background:'var(--surface, #fff)',
                      opacity:(loading || (!!demoRole && !busy)) ? 0.55 : 1,
                      transition:'border-color .12s, background .12s',
                    }}
                    onMouseEnter={e=>{ if(!demoRole){ e.currentTarget.style.borderColor=NCARE.accent; e.currentTarget.style.background=NCARE.soft; }}}
                    onMouseLeave={e=>{ e.currentTarget.style.borderColor='var(--line)'; e.currentTarget.style.background='var(--surface, #fff)'; }}
                  >
                    <span style={{width:30, height:30, flex:'0 0 auto', display:'grid', placeItems:'center', fontSize:16, borderRadius:8, background:NCARE.soft, color:NCARE.navy2}}>
                      {busy ? '…' : d.icon}
                    </span>
                    <span style={{minWidth:0}}>
                      <span style={{display:'block', fontSize:12.5, fontWeight:700, color:'var(--ink-1)'}}>{d.label}</span>
                      <span style={{display:'block', fontSize:10.5, color:'var(--ink-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{busy ? '로그인 중…' : d.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p style={{fontSize:10.5, color:'var(--ink-3)', marginTop:10, textAlign:'center'}}>
              {region.name} · 선택 즉시 해당 유형의 체험 계정으로 로그인됩니다.
            </p>
          </div>

          <div className="auth-foot">
            <div className="auth-foot-row">
              <span className="ck">{Ic.check}</span>
              <span>역할(케어매니저·유관기관·보호자·관리자)에 맞는 화면으로 자동 이동합니다.</span>
            </div>
            <div className="auth-foot-row" style={{marginTop:10}}>
              <span className="ck">{Ic.check}</span>
              <span>모든 접근 기록은 5년간 감사 로그로 안전하게 보존됩니다.</span>
            </div>
          </div>
        </div>
      </form>
    </div>
    <AppFooter/>
    </div>
  );
}
export { Auth };
